import React, { useState, useEffect, useRef, useCallback } from "react";
import { FileText, X, ChevronLeft, ChevronRight } from "lucide-react";
import { getUserInitials } from "../../utils/avatarUtils";
import StatusBadge from "../common/StatusBadge";
import { apiFetch } from "../../../../context/AuthContext";
import { API_URL } from "../../config";
import { FIELD_SECTIONS, SECTION_ICONS, MONEY_FIELDS } from "../../constants/fieldSections";

// ===== RESPONSIVE VIEW EMPLOYEE MODAL COMPONENT =====
//
// Adapted from the admin Employee Management page's ViewEmployeeModal so
// both pages show the same field catalog, the same tabbed layout, and the
// same full-record fetch/loading/error handling. Two differences from the
// admin version, both deliberate:
//   1. Prop names match this page's existing wiring (onClose, isDarkMode,
//      etc.) instead of the admin version's (setShowViewEmployee, theme) —
//      so EmployeeManagementManager.jsx didn't need a rewrite to adopt this.
//   2. There is no document checklist section — documents are admin-only
//      and hidden from managers (see constants/fieldSections.js).
//
// The tab bar itself is also a real memory/perf win over the previous
// version: that one rendered every section's fields (~90 fields across 9
// sections) into the DOM at once, stacked vertically, for every employee
// opened. This mounts one section (~10-15 fields) at a time.
const ViewEmployeeModal = ({
  styles,
  text,
  language,
  screenSize,
  fonts,
  spacing,
  isDarkMode,
  selectedEmployee,
  employeeProfilePics,
  onClose,
}) => {
  const infinityCode =
    selectedEmployee?.fullEmployeeData?.infinityCode ||
    selectedEmployee?.infinityCode ||
    selectedEmployee?.id ||
    null;

  // Seed with the row's locally loaded record; the complete record (all
  // database fields) is fetched right after mount and replaces it.
  const [fullRecord, setFullRecord] = useState(null);
  const [isLoadingFullRecord, setIsLoadingFullRecord] = useState(false);
  const [fullRecordError, setFullRecordError] = useState(null);
  const [activeTab, setActiveTab] = useState(FIELD_SECTIONS[0].key);
  const [showFullImage, setShowFullImage] = useState(false);
  const tabBarRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Check which arrow buttons should be visible
  const updateScrollButtons = useCallback(() => {
    const el = tabBarRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = tabBarRef.current;
    if (!el) return;
    updateScrollButtons();
    el.addEventListener("scroll", updateScrollButtons, { passive: true });
    const ro = new ResizeObserver(updateScrollButtons);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollButtons);
      ro.disconnect();
    };
  }, [updateScrollButtons]);

  const scrollTabs = (dir) => {
    const el = tabBarRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 220, behavior: "smooth" });
  };

  useEffect(() => {
    if (!infinityCode) return;
    let cancelled = false;

    const loadFullRecord = async () => {
      try {
        setIsLoadingFullRecord(true);
        setFullRecordError(null);
        const response = await apiFetch(
          `${API_URL}/employees/select/${infinityCode}`,
          { method: "GET" }
        );
        const json = await response.json().catch(() => null);
        const record = json?.employee || null;
        if (!response.ok || !record) {
          throw new Error(json?.message || "Failed to load employee record");
        }
        if (!cancelled) setFullRecord(record);
      } catch (err) {
        console.error("Failed to fetch full employee record:", err);
        if (!cancelled) setFullRecordError(err.message);
      } finally {
        if (!cancelled) setIsLoadingFullRecord(false);
      }
    };

    loadFullRecord();
    return () => {
      cancelled = true;
    };
  }, [infinityCode]);

  // Reset to the first tab whenever a different employee is opened, so the
  // modal doesn't reopen on whatever tab the previous employee left active.
  useEffect(() => {
    setActiveTab(FIELD_SECTIONS[0].key);
  }, [infinityCode]);

  // Return null if no employee is selected (after hooks)
  if (!selectedEmployee) return null;

  // Merge the fetched complete record over the locally loaded row data
  // (with aliases for keys the local mapper renamed).
  const local = selectedEmployee.fullEmployeeData || {};
  const employeeData = {
    ...local,
    dob: local.dateOfBirth,
    emergencyPhoneNumber: local.emergencyPhone,
    created_at: local.createdAt,
    updated_at: local.updatedAt,
    sr: selectedEmployee.name,
    email: selectedEmployee.email,
    phoneNumber: selectedEmployee.phone,
    employeeState: selectedEmployee.status,
    hiringDate: selectedEmployee.joinDate,
    infinityCode: selectedEmployee.id,
    ...(fullRecord || {}),
  };

  // Function to format date strings
  const formatDate = (dateString) => {
    if (!dateString) return text[language].na;
    const parsed = new Date(dateString);
    if (isNaN(parsed.getTime())) return text[language].na;
    return parsed.toLocaleDateString("en-US", {
      year: "numeric",
      month: screenSize === "xs" ? "short" : "long",
      day: "numeric",
    });
  };

  // Function to format currency amounts
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === "" || amount === 0)
      return text[language].na;
    const numeric = Number(amount);
    if (isNaN(numeric)) return text[language].na;
    return `$${numeric.toLocaleString()}`;
  };

  const renderBooleanValue = (value) => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        background: value ? "rgba(16,185,129,0.12)" : "rgba(148,163,184,0.12)",
        color: value ? "#059669" : isDarkMode ? "#94a3b8" : "#64748b",
      }}
    >
      {value
        ? language === "ar" ? "نعم ✓" : "Yes ✓"
        : language === "ar" ? "لا ✗" : "No ✗"}
    </span>
  );

  // Resolve a display value for any field of the shared catalog
  const renderFieldValue = (field) => {
    const value = employeeData?.[field.name];
    if (field.type === "boolean") return renderBooleanValue(Boolean(value));
    if (field.type === "date") return formatDate(value);
    if (field.type === "department")
      return String(
        employeeData?.department?.name ??
          selectedEmployee?.department ??
          text[language].na
      );
    if (MONEY_FIELDS.has(field.name)) return formatCurrency(value);
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      value === "N/A"
    )
      return text[language].na;
    return String(value);
  };

  const handleModalClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  // Return modal JSX
  return (
    <>
    <div style={styles.modal} onClick={handleModalClick}>
      <div style={styles.viewModalContent} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{text[language].employeeDetails}</h2>
          <button style={styles.closeBtn} onClick={onClose}>
            <X size={screenSize === "xs" ? 20 : 24} />
          </button>
        </div>

        {/* Full-record loading / error banner */}
        {isLoadingFullRecord && (
          <div
            style={{
              marginBottom: spacing.margin,
              padding: "10px 14px",
              borderRadius: 10,
              background: isDarkMode ? "rgba(46,124,186,0.12)" : "rgba(46,124,186,0.08)",
              color: "#2e7cba",
              fontSize: fonts.small,
              fontWeight: 600,
            }}
          >
            {language === "ar"
              ? "جاري تحميل كامل بيانات الموظف..."
              : "Loading the complete employee record..."}
          </div>
        )}
        {fullRecordError && !isLoadingFullRecord && (
          <div
            style={{
              marginBottom: spacing.margin,
              padding: "10px 14px",
              borderRadius: 10,
              background: isDarkMode ? "rgba(239,68,68,0.12)" : "rgba(239,68,68,0.08)",
              color: "#ef4444",
              fontSize: fonts.small,
              fontWeight: 600,
            }}
          >
            {language === "ar"
              ? "تعذر تحميل كامل البيانات — يتم عرض البيانات المتاحة فقط"
              : "Could not load the full record — showing locally available data only"}
          </div>
        )}

        {/* Modal Body */}
        <div style={styles.viewModalBody}>
          {/* Employee Header Section */}
          <div style={{
            ...styles.employeeHeader,
            flexDirection: 'row',
            alignItems: 'center',
            textAlign: language === 'ar' ? 'right' : 'left'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing.margin,
              flex: 1
            }}>
              {/* Profile Picture */}
              {employeeProfilePics[selectedEmployee.id] ? (
                <img
                  src={employeeProfilePics[selectedEmployee.id]}
                  alt={selectedEmployee.name}
                  style={{
                    ...styles.largeAvatar,
                    objectFit: 'cover',
                    cursor: 'pointer'
                  }}
                  onClick={() => setShowFullImage(true)}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextSibling?.style) e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}

              <div
                style={{
                  ...styles.largeAvatar,
                  display: employeeProfilePics[selectedEmployee.id] ? 'none' : 'flex'
                }}
              >
                {getUserInitials(selectedEmployee?.name)}
              </div>

              {/* Employee Info */}
              <div style={{
                ...styles.employeeHeaderInfo,
                textAlign: language === 'ar' ? 'right' : 'left'
              }}>
                <h3 style={styles.employeeName}>{selectedEmployee?.name}</h3>
                <p style={styles.employeeTitle}>
                  {employeeData?.position ?? selectedEmployee?.position}
                </p>
                <p style={styles.employeeDepartment}>
                  {employeeData?.department?.name ?? selectedEmployee?.department}
                </p>
                <div style={styles.employeeStatus}>
                  <StatusBadge
                    status={employeeData?.employeeState ?? selectedEmployee?.status}
                    screenSize={screenSize}
                    fonts={fonts}
                    text={text}
                    language={language}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Section tabs — [◀] [scrollable strip] [▶] inline row, no scrollbar */}
          <div style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            marginBottom: spacing.margin,
          }}>
            {/* Left arrow — inline, collapses when not needed */}
            <button
              type="button"
              onClick={() => scrollTabs(-1)}
              style={{
                flexShrink: 0,
                opacity: canScrollLeft ? 1 : 0,
                pointerEvents: canScrollLeft ? "auto" : "none",
                width: canScrollLeft ? 36 : 0,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 36,
                borderRadius: "50%",
                border: isDarkMode
                  ? "1px solid rgba(96,165,250,0.35)"
                  : "1px solid rgba(46,124,186,0.28)",
                background: isDarkMode
                  ? "rgba(30,41,59,0.95)"
                  : "rgba(255,255,255,0.98)",
                color: isDarkMode ? "#93c5fd" : "#2e7cba",
                cursor: "pointer",
                boxShadow: isDarkMode
                  ? "0 2px 14px rgba(0,0,0,0.5)"
                  : "0 2px 12px rgba(0,0,0,0.14)",
                transition: "opacity 0.2s ease, width 0.2s ease, transform 0.15s ease, background 0.15s ease",
                backdropFilter: "blur(10px)",
                padding: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.1)";
                e.currentTarget.style.background = isDarkMode ? "rgba(96,165,250,0.18)" : "rgba(46,124,186,0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.background = isDarkMode ? "rgba(30,41,59,0.95)" : "rgba(255,255,255,0.98)";
              }}
            >
              <ChevronLeft size={18} />
            </button>

            {/* Scrollable tab strip — takes all available space, scrollbar hidden in ALL browsers */}
            <div
              ref={tabBarRef}
              style={{ ...styles.tabBar, flex: 1, minWidth: 0 }}
              className="view-tabs-scroll"
            >
              {FIELD_SECTIONS.map((section) => {
                const TabIcon = SECTION_ICONS[section.key] || FileText;
                const isActive = section.key === activeTab;
                return (
                  <button
                    key={section.key}
                    type="button"
                    onClick={() => setActiveTab(section.key)}
                    onMouseEnter={(e) => {
                      if (isActive) return;
                      Object.assign(e.currentTarget.style, styles.tabButtonHover);
                    }}
                    onMouseLeave={(e) => {
                      if (isActive) return;
                      e.currentTarget.style.background = styles.tabButton.background;
                      e.currentTarget.style.color = styles.tabButton.color;
                    }}
                    style={{
                      ...styles.tabButton,
                      ...(isActive ? styles.tabButtonActive : {}),
                    }}
                  >
                    <TabIcon size={14} />
                    <span>{section.title[language] || section.title.en}</span>
                  </button>
                );
              })}
            </div>

            {/* Right arrow — inline, collapses when not needed */}
            <button
              type="button"
              onClick={() => scrollTabs(1)}
              style={{
                flexShrink: 0,
                opacity: canScrollRight ? 1 : 0,
                pointerEvents: canScrollRight ? "auto" : "none",
                width: canScrollRight ? 36 : 0,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 36,
                borderRadius: "50%",
                border: isDarkMode
                  ? "1px solid rgba(96,165,250,0.35)"
                  : "1px solid rgba(46,124,186,0.28)",
                background: isDarkMode
                  ? "rgba(30,41,59,0.95)"
                  : "rgba(255,255,255,0.98)",
                color: isDarkMode ? "#93c5fd" : "#2e7cba",
                cursor: "pointer",
                boxShadow: isDarkMode
                  ? "0 2px 14px rgba(0,0,0,0.5)"
                  : "0 2px 12px rgba(0,0,0,0.14)",
                transition: "opacity 0.2s ease, width 0.2s ease, transform 0.15s ease, background 0.15s ease",
                backdropFilter: "blur(10px)",
                padding: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.1)";
                e.currentTarget.style.background = isDarkMode ? "rgba(96,165,250,0.18)" : "rgba(46,124,186,0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.background = isDarkMode ? "rgba(30,41,59,0.95)" : "rgba(255,255,255,0.98)";
              }}
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Active tab's fields only — the rest of the catalog isn't mounted */}
          {(() => {
            const section =
              FIELD_SECTIONS.find((s) => s.key === activeTab) || FIELD_SECTIONS[0];
            const SectionIcon = SECTION_ICONS[section.key] || FileText;
            return (
              <div style={styles.infoSection}>
                <div style={styles.sectionHeader}>
                  <SectionIcon size={screenSize === "xs" ? 16 : 18} />
                  <span>{section.title[language] || section.title.en}</span>
                </div>
                <div style={styles.infoGrid}>
                  {section.fields.map((field) => (
                    <div key={field.name} style={styles.infoItem}>
                      <div style={{ width: "100%" }}>
                        <div style={styles.infoLabel}>
                          {field.label[language] || field.label.en}
                        </div>
                        <div style={styles.infoValue}>
                          {renderFieldValue(field)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      <style>{`
        /*
         * Hide horizontal scrollbar across ALL browsers.
         * Edge Chromium sometimes ignores display:none — using height:0 + appearance:none
         * is the most robust approach for Chromium-based browsers.
         */
        .view-tabs-scroll {
          scrollbar-width: none !important;       /* Firefox */
          -ms-overflow-style: none !important;    /* IE 10-11, old Edge */
        }
        .view-tabs-scroll::-webkit-scrollbar {
          height: 0 !important;                   /* Chrome, Safari, Edge, Opera */
          width: 0 !important;
          appearance: none !important;
          -webkit-appearance: none !important;
          background: transparent !important;
        }
        .view-tabs-scroll::-webkit-scrollbar-track,
        .view-tabs-scroll::-webkit-scrollbar-thumb,
        .view-tabs-scroll::-webkit-scrollbar-corner {
          display: none !important;
          background: transparent !important;
        }
      `}</style>
    </div>

    {/* Fullscreen image lightbox */}
    {showFullImage && employeeProfilePics[selectedEmployee.id] && (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '2rem',
          cursor: 'zoom-out',
        }}
        onClick={() => setShowFullImage(false)}
      >
        <img
          src={employeeProfilePics[selectedEmployee.id]}
          alt={selectedEmployee.name}
          style={{
            width: 'min(70vw, 70vh, 32rem)',
            height: 'min(70vw, 70vh, 32rem)',
            borderRadius: '50%',
            objectFit: 'cover',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            cursor: 'default',
          }}
          onClick={(e) => e.stopPropagation()}
        />
        <button
          onClick={() => setShowFullImage(false)}
          style={{
            position: 'fixed',
            top: '1.5rem',
            [language === 'ar' ? 'left' : 'right']: '1.5rem',
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '50%',
            width: '2.75rem',
            height: '2.75rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            cursor: 'pointer',
          }}
        >
          <X size={24} />
        </button>
      </div>
    )}
    </>
  );
};

export default ViewEmployeeModal;
